/**
 * useNavigation — combines navConfig + permissions + preferences
 * for rendering the sidebar.
 *
 * Phases:
 * - Step A (current): role-based filtering
 * - Step B (target): permission-key filtering via effective-access
 * - Capabilities: feature-flag gating via capability registry
 */

import { useMemo, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useEffectiveAccess } from '@/hooks/useQueryHooks';
import { preferencesService } from '@/services/preferences.service';
import {
  navItems,
  getGroupsForShell,
  type NavShell,
  type NavItemConfig,
  type NavGroupConfig,
} from '@/config/nav';
import { registeredCapabilities } from '@/config/capabilities';
import { hasConsent } from '@/lib/cookieConsent';
import type { UserRole } from '@amuster/types';

const MAX_RECENT = 10;
const DEBOUNCE_MS = 600;

interface NavPrefs {
  starred: string[];
  recent: string[];
  pinnedOrder: string[];
}

function getNavPrefs(uiPrefs: Record<string, unknown> | undefined): NavPrefs {
  const nav = (uiPrefs?.nav ?? {}) as Record<string, unknown>;
  return {
    starred: Array.isArray(nav.starred) ? (nav.starred as string[]) : [],
    recent: Array.isArray(nav.recent) ? (nav.recent as string[]) : [],
    pinnedOrder: Array.isArray(nav.pinnedOrder) ? (nav.pinnedOrder as string[]) : [],
  };
}

function getLocalNavPrefs(): NavPrefs {
  if (!hasConsent('functional')) return { starred: [], recent: [], pinnedOrder: [] };
  try {
    const raw = localStorage.getItem('amuster_nav_prefs');
    if (raw) return JSON.parse(raw) as NavPrefs;
  } catch { /* ignore */ }
  return { starred: [], recent: [], pinnedOrder: [] };
}

function setLocalNavPrefs(prefs: NavPrefs) {
  if (!hasConsent('functional')) return;
  try {
    localStorage.setItem('amuster_nav_prefs', JSON.stringify(prefs));
  } catch { /* ignore */ }
}

export interface UseNavigationResult {
  /** Ordered groups for this shell */
  groups: NavGroupConfig[];
  /** Items grouped by group ID, filtered by role/permission/capability */
  groupedItems: Map<string, NavItemConfig[]>;
  /** Rail items for this shell (filtered) */
  railItems: NavItemConfig[];
  /** User's starred items (filtered, ordered by preference) */
  starredItems: NavItemConfig[];
  /** Recently visited items (filtered) */
  recentItems: NavItemConfig[];
  /** True while effective-access is loading */
  isLoading: boolean;
  /** Toggle an item's starred status */
  toggleStarred: (itemId: string) => void;
  /** Record a visit to a nav item */
  trackVisit: (itemId: string) => void;
  /** Move a starred item (focus + "Position X of Y" announcement) */
  moveStarred: (itemId: string, direction: 'up' | 'down' | 'top' | 'bottom') => void;
}

export function useNavigation(shell: NavShell): UseNavigationResult {
  const { user, preferences, plan } = useAuth();
  const userId = user?.id;
  const userRole = user?.role as UserRole | undefined;
  const { data: effectiveAccess, isLoading: accessLoading } = useEffectiveAccess(userId ?? '');

  // Build permission set from effective access
  const permissionSet = useMemo(() => {
    if (!effectiveAccess?.data) return new Set<string>();
    const entries = effectiveAccess.data as Array<{ permission_key: string }>;
    return new Set(entries.map((e) => e.permission_key));
  }, [effectiveAccess]);

  // Get nav preferences (server prefs with localStorage fallback)
  const navPrefs = useMemo(() => {
    if (preferences?.ui_prefs) {
      const prefs = getNavPrefs(preferences.ui_prefs);
      setLocalNavPrefs(prefs); // sync localStorage
      return prefs;
    }
    return getLocalNavPrefs();
  }, [preferences]);

  // Debounced preference persistence
  const pendingWrite = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestPrefs = useRef(navPrefs);
  latestPrefs.current = navPrefs;
  const prefsRef = useRef(preferences);
  prefsRef.current = preferences;

  const persistPrefs = useCallback((updated: NavPrefs) => {
    setLocalNavPrefs(updated);
    if (pendingWrite.current) clearTimeout(pendingWrite.current);
    pendingWrite.current = setTimeout(() => {
      const existing = prefsRef.current?.ui_prefs ?? {};
      preferencesService.updatePreferences({
        ui_prefs: { ...existing, nav: updated },
      }).catch(console.error);
      pendingWrite.current = null;
    }, DEBOUNCE_MS);
  }, []);

  // Flush pending writes on unmount (shell switch) and beforeunload (tab close).
  // Correctness invariants:
  //   1. localStorage is always written synchronously (never stale)
  //   2. Server write debounced at DEBOUNCE_MS — last write wins
  //   3. Undo (re-toggle) cancels pending server write via clearTimeout
  //   4. Unmount flushes immediately via axios (SPA still alive)
  //   5. beforeunload flushes via fetch+keepalive (survives page teardown)
  useEffect(() => {
    const buildPayload = () => {
      const existing = prefsRef.current?.ui_prefs ?? {};
      return { ui_prefs: { ...existing, nav: latestPrefs.current } };
    };

    const flushPending = (useKeepalive: boolean) => {
      if (!pendingWrite.current) return;
      clearTimeout(pendingWrite.current);
      pendingWrite.current = null;

      if (useKeepalive) {
        // fetch with keepalive: true survives page unload (64 KB limit — nav prefs are tiny)
        const csrf = document.cookie.match(/(?:^|;\s*)amuster_csrf=([^;]*)/)?.[1];
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-Use-Cookies': 'true',
        };
        if (csrf) headers['X-CSRF-Token'] = csrf;
        fetch('/api/auth/preferences', {
          method: 'PUT',
          headers,
          body: JSON.stringify(buildPayload()),
          credentials: 'include',
          keepalive: true,
        }).catch(() => {}); // Best-effort; localStorage already has the data
      } else {
        // SPA still alive (shell switch / React unmount) — axios is fine
        preferencesService.updatePreferences(buildPayload()).catch(console.error);
      }
    };

    const handleBeforeUnload = () => flushPending(true);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      flushPending(false);
    };
  }, []);

  // Derive enabled capabilities from the tenant's plan features.
  // Only capabilities that are (a) registered in the capability registry AND
  // (b) enabled (true) in plan.features are considered active.
  // This ensures nav items gated by capabilities (e.g. module_asset_active)
  // are hidden when the tenant plan does not include them.
  const enabledCapabilities = useMemo(() => {
    const features = (plan as { features?: Record<string, boolean> } | null)?.features;
    if (!features) return new Set<string>();
    const enabled = new Set<string>();
    for (const [key, value] of Object.entries(features)) {
      if (value === true && registeredCapabilities.has(key)) {
        enabled.add(key);
      }
    }
    return enabled;
  }, [plan]);

  // central_admin is the highest tenant role — unrestricted nav visibility.
  // Capabilities (feature flags) still apply since they represent tenant subscription,
  // but role/permission_key checks are bypassed.
  const isUnrestrictedRole = userRole === 'central_admin';

  // Filter items by role + permission_keys + capabilities
  const filterItem = useCallback(
    (item: NavItemConfig): boolean => {
      // Capability check: applies to ALL roles (it's a tenant feature flag, not a permission)
      if (item.capabilities?.length) {
        const allEnabled = item.capabilities.every((c) => enabledCapabilities.has(c));
        if (!allEnabled) return false;
      }

      // Unrestricted roles skip permission + role checks
      if (isUnrestrictedRole) return true;

      // Permission-key check (Step B): if item has permission_keys, check against effective-access
      if (item.permission_keys?.length) {
        return item.permission_keys.some((k) => permissionSet.has(k));
      }

      // Role-based check (Step A): fallback if no permission_keys set
      if (item.roles?.length) {
        return !!userRole && item.roles.includes(userRole);
      }

      // No restrictions: visible to all
      return true;
    },
    [userRole, isUnrestrictedRole, permissionSet, enabledCapabilities],
  );

  // Compute filtered + grouped items
  const groups = useMemo(() => getGroupsForShell(shell), [shell]);

  const groupedItems = useMemo(() => {
    const shellItems = navItems.filter((i) => i.shell === shell);
    const grouped = new Map<string, NavItemConfig[]>();
    for (const item of shellItems) {
      if (!filterItem(item)) continue;
      const list = grouped.get(item.group) || [];
      list.push(item);
      grouped.set(item.group, list);
    }
    return grouped;
  }, [shell, filterItem]);

  const railItems = useMemo(
    () => navItems.filter((i) => i.shell === shell && i.railItem && filterItem(i)),
    [shell, filterItem],
  );

  // Starred items (ordered by user preference)
  const starredItems = useMemo(() => {
    const result: NavItemConfig[] = [];
    for (const id of navPrefs.starred) {
      const item = navItems.find((i) => i.id === id);
      if (item && filterItem(item)) result.push(item);
    }
    return result;
  }, [navPrefs.starred, filterItem]);

  // Recent items
  const recentItems = useMemo(() => {
    const result: NavItemConfig[] = [];
    for (const id of navPrefs.recent) {
      const item = navItems.find((i) => i.id === id);
      if (item && filterItem(item)) result.push(item);
    }
    return result.slice(0, MAX_RECENT);
  }, [navPrefs.recent, filterItem]);

  const toggleStarred = useCallback(
    (itemId: string) => {
      const current = [...latestPrefs.current.starred];
      const idx = current.indexOf(itemId);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(itemId);
      }
      const updated = { ...latestPrefs.current, starred: current };
      latestPrefs.current = updated;
      persistPrefs(updated);
    },
    [persistPrefs],
  );

  const trackVisit = useCallback(
    (itemId: string) => {
      const current = latestPrefs.current.recent.filter((id) => id !== itemId);
      current.unshift(itemId);
      const updated = { ...latestPrefs.current, recent: current.slice(0, MAX_RECENT) };
      latestPrefs.current = updated;
      persistPrefs(updated);
    },
    [persistPrefs],
  );

  const moveStarred = useCallback(
    (itemId: string, direction: 'up' | 'down' | 'top' | 'bottom') => {
      const current = [...latestPrefs.current.starred];
      const idx = current.indexOf(itemId);
      if (idx < 0) return;

      current.splice(idx, 1);
      switch (direction) {
        case 'up':
          current.splice(Math.max(0, idx - 1), 0, itemId);
          break;
        case 'down':
          current.splice(Math.min(current.length, idx + 1), 0, itemId);
          break;
        case 'top':
          current.unshift(itemId);
          break;
        case 'bottom':
          current.push(itemId);
          break;
      }

      const updated = { ...latestPrefs.current, starred: current };
      latestPrefs.current = updated;
      persistPrefs(updated);
    },
    [persistPrefs],
  );

  return {
    groups,
    groupedItems,
    railItems,
    starredItems,
    recentItems,
    isLoading: accessLoading,
    toggleStarred,
    trackVisit,
    moveStarred,
  };
}
